import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IEngine, IEngineMapping } from '~/types/arbitration'

import { DevReviewEngine } from '../Engines'

import { updateReviewEngine } from '~/api/arbitration'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import { arbitrationKeys, useEngineQuery } from '~/queries/arbitration'
import { useGetMeAsUserQuery } from '~/queries/users'
import { ENGINE_TYPE } from '~/types/arbitration'
import { displayToast } from '~/utils/toast'

interface Props {
  selectedApp: IAppSummary
}

const ReviewEngine = ({ selectedApp }: Props): ReactElement => {
  const queryClient = useQueryClient()

  const { data: user } = useGetMeAsUserQuery()

  const { data: engine, isLoading: isEngineLoading } = useEngineQuery({
    appKey: selectedApp.app_key,
    deviceType: selectedApp.device_type,
    engineType: ENGINE_TYPE.REVIEW,
  })

  const { mutateAsync: updateReviewEngineMutation } = useMutation({ mutationFn: updateReviewEngine })

  const handleSubmit = async (mapping: IEngineMapping, version?: string) => {
    try {
      await updateReviewEngineMutation({
        appKey: selectedApp.app_key,
        deviceType: selectedApp.device_type,
        mapping,
        updatedBy: user?.user_id,
        version,
      })
      queryClient.invalidateQueries({
        queryKey: arbitrationKeys.engine(ENGINE_TYPE.REVIEW, selectedApp.app_key, selectedApp.device_type),
      })

      displayToast('Engine successfully updated', 'success')
    } catch {
      displayToast('Engine could not be updated', 'error')
    }
  }

  if (isEngineLoading) {
    return <MwMLoader category="item" />
  }

  return (
    <Layout>
      <DevReviewEngine
        engine={engine as IEngine}
        isReviewEngine
        onSubmit={handleSubmit}
        selectedApp={selectedApp}
        version={(engine as IEngine)?.app_version}
      />
    </Layout>
  )
}

export default ReviewEngine
